import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import {
  FiUserX,
  FiUserCheck,
  FiSearch,
  FiPlus,
  FiTrash2,
  FiEdit,
  FiAlertTriangle,
  FiGlobe,
  FiHome,
  FiShield,
  FiX,
  FiCheckCircle,
  FiInfo,
  FiRefreshCw,
  FiCalendar,
  FiFilter,
  FiChevronLeft,
  FiChevronRight
} from 'react-icons/fi';
import { Link } from 'react-router-dom';

const Blacklist = () => {
  const { user } = useAuth();
  const [blacklistedClients, setBlacklistedClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [formData, setFormData] = useState({
    clientId: '',
    reason: '',
    notes: ''
  });

  // New state for global blacklist
  const [viewMode, setViewMode] = useState('local'); // 'local' or 'global'
  const [agencies, setAgencies] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState('');
  const [hasGlobalAccess, setHasGlobalAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkGlobalBlacklistAccess();
  }, []); // Only run once on component mount

  useEffect(() => {
    if (checkingAccess) return; // Don't fetch data while checking access

    if (viewMode === 'local') {
      fetchBlacklistedClients();
    } else if (viewMode === 'global' && hasGlobalAccess) {
      fetchGlobalBlacklistedClients();
      fetchAgencies();
    }
  }, [currentPage, searchTerm, viewMode, selectedAgency, hasGlobalAccess, checkingAccess]);

  useEffect(() => {
    if (!checkingAccess) {
      fetchClients();
    }
  }, [checkingAccess]);

  const checkGlobalBlacklistAccess = async () => {
    try {
      setCheckingAccess(true);
      const response = await api.get('/api/blacklist/access/check');
      setHasGlobalAccess(response.data.hasAccess);

      // If no global access, force local view
      if (!response.data.hasAccess && viewMode === 'global') {
        setViewMode('local');
        toast.error('Votre agence n\'a pas accès à la liste noire globale. Contactez le superadmin.');
      }
    } catch (error) {
      console.error('Error checking global blacklist access:', error);
      setHasGlobalAccess(false);
      if (viewMode === 'global') {
        setViewMode('local');
      }
    } finally {
      setCheckingAccess(false);
    }
  };

  const fetchBlacklistedClients = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors

      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        search: searchTerm
      });

      const response = await api.get(`/api/blacklist?${params}`);

      if (response.data && response.data.data) {
        setBlacklistedClients(response.data.data);
        setTotalPages(response.data.pagination?.totalPages || 1);
      } else {
        console.error('Invalid response format:', response.data);
        const errorMsg = 'Format de réponse invalide du serveur';
        setError(errorMsg);
        toast.error(errorMsg);
        setBlacklistedClients([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching blacklisted clients:', error);

      let errorMsg = 'Erreur lors de la récupération des clients blacklistés';

      if (error.response) {
        // Server responded with error status
        if (error.response.status === 401) {
          errorMsg = 'Session expirée. Veuillez vous reconnecter.';
        } else if (error.response.status === 403) {
          errorMsg = 'Accès refusé. Vérifiez vos permissions.';
        } else if (error.response.status === 500) {
          errorMsg = 'Erreur serveur. Veuillez réessayer plus tard.';
        } else {
          errorMsg = `Erreur ${error.response.status}: ${error.response.data?.message || 'Erreur lors de la récupération des clients blacklistés'}`;
        }
      } else if (error.request) {
        // Network error
        errorMsg = 'Erreur de connexion. Vérifiez votre connexion internet.';
      }

      setError(errorMsg);
      toast.error(errorMsg);
      setBlacklistedClients([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/api/clients');
      setClients(response.data.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleAddToBlacklist = async () => {
    try {
      if (!formData.clientId || !formData.reason.trim()) {
        toast.error('Veuillez sélectionner un client et fournir une raison');
        return;
      }

      const response = await api.post('/api/blacklist', formData);
      toast.success(response.data.message);
      setShowAddModal(false);
      setFormData({ clientId: '', reason: '', notes: '' });
      fetchBlacklistedClients();
    } catch (error) {
      console.error('Error adding client to blacklist:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de l\'ajout à la liste noire');
    }
  };

  const handleRemoveFromBlacklist = async (blacklistId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir retirer ce client de la liste noire ?')) {
      return;
    }

    try {
      const response = await api.put(`/api/blacklist/${blacklistId}/remove`);
      toast.success(response.data.message);
      fetchBlacklistedClients();
    } catch (error) {
      console.error('Error removing client from blacklist:', error);
      toast.error('Erreur lors du retrait de la liste noire');
    }
  };

  const handleClientSearch = (term) => {
    setClientSearchTerm(term);
    setShowClientDropdown(term.length > 0);
  };

  const handleClientSelect = (client) => {
    setFormData({ ...formData, clientId: client._id });
    setClientSearchTerm(`${client.name} - ${client.email}`);
    setShowClientDropdown(false);
  };

  const filteredClients = clients.filter(client => {
    if (!clientSearchTerm.trim()) return false;

    const searchTerm = clientSearchTerm.toLowerCase().trim();
    const clientName = (client.name || '').toLowerCase();
    const clientEmail = (client.email || '').toLowerCase();
    const clientPhone = (client.phone || '').toLowerCase();

    return clientName.includes(searchTerm) ||
      clientEmail.includes(searchTerm) ||
      clientPhone.includes(searchTerm);
  }).slice(0, 10);

  const fetchAgencies = async () => {
    try {
      const response = await api.get('/api/blacklist/agencies');

      if (response.data && response.data.data) {
        setAgencies(response.data.data);
      } else {
        console.error('Invalid agencies response format:', response.data);
        setAgencies([]);
      }
    } catch (error) {
      console.error('Error fetching agencies:', error);

      if (error.response?.status === 403) {
        console.log('Access denied to agencies endpoint - this is normal for non-superadmin users');
      } else {
        toast.error('Erreur lors de la récupération des agences');
      }

      setAgencies([]);
    }
  };

  const fetchGlobalBlacklistedClients = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        search: searchTerm
      });

      if (selectedAgency) {
        params.append('agencyFilter', selectedAgency);
      }

      const response = await api.get(`/api/blacklist/global?${params}`);

      if (response.data && response.data.data) {
        setBlacklistedClients(response.data.data);
        setTotalPages(response.data.pagination?.totalPages || 1);
      } else {
        console.error('Invalid global response format:', response.data);
        toast.error('Format de réponse invalide pour la liste noire globale');
        setBlacklistedClients([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching global blacklisted clients:', error);

      if (error.response) {
        // Server responded with error status
        if (error.response.status === 401) {
          toast.error('Session expirée. Veuillez vous reconnecter.');
        } else if (error.response.status === 403) {
          toast.error('Accès refusé à la liste noire globale. Vérifiez vos permissions.');
        } else if (error.response.status === 500) {
          toast.error('Erreur serveur. Veuillez réessayer plus tard.');
        } else {
          toast.error(`Erreur ${error.response.status}: ${error.response.data?.message || 'Erreur lors de la récupération de la liste noire globale'}`);
        }
      } else if (error.request) {
        // Network error
        toast.error('Erreur de connexion. Vérifiez votre connexion internet.');
      } else {
        // Other error
        toast.error('Erreur lors de la récupération de la liste noire globale');
      }

      setBlacklistedClients([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="relative">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-200 border-t-red-600"></div>
          <FiUserX className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-5 h-5 text-red-600" />
        </div>
        <p className="text-gray-500 dark:text-gray-400 animate-pulse">Chargement de la liste noire...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-gradient-to-br from-red-500 to-rose-600 rounded-lg sm:rounded-xl shadow-lg">
              <FiUserX className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
            </div>
            Liste Noire
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            Gérez les clients blacklistés de votre agence
          </p>
          {/* Access Level Badge */}
          <div className="mt-3 flex items-center gap-2">
            {hasGlobalAccess ? (
              <span className="inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-gradient-to-r from-brand-green/10 to-emerald-500/10 text-brand-green dark:text-green-400 text-xs sm:text-sm font-medium rounded-full border border-brand-green/20">
                <FiGlobe className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Accès global activé</span>
                <span className="sm:hidden">Global</span>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs sm:text-sm font-medium rounded-full border border-amber-200 dark:border-amber-700">
                <FiHome className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Accès local uniquement</span>
                <span className="sm:hidden">Local</span>
              </span>
            )}
          </div>
        </div>

        {/* Refresh Button */}
        <button
          onClick={() => {
            if (viewMode === 'local') {
              fetchBlacklistedClients();
            } else if (viewMode === 'global' && hasGlobalAccess) {
              fetchGlobalBlacklistedClients();
            }
          }}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-all text-sm w-fit"
        >
          <FiRefreshCw className="w-4 h-4" />
          <span className="hidden sm:inline">Actualiser</span>
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-5">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-red-100 dark:bg-red-900/50 rounded-xl">
              <FiAlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div className="flex-1">
              <p className="text-base font-semibold text-red-800 dark:text-red-200">
                Erreur de récupération des données
              </p>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                {error}
              </p>
            </div>
            <button
              onClick={() => {
                setError(null);
                if (viewMode === 'local') {
                  fetchBlacklistedClients();
                } else if (viewMode === 'global' && hasGlobalAccess) {
                  fetchGlobalBlacklistedClients();
                }
              }}
              className="px-4 py-2 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 font-medium rounded-lg hover:bg-red-200 dark:hover:bg-red-900 transition-colors text-sm"
            >
              Réessayer
            </button>
          </div>
        </div>
      )}

      {/* View Mode Toggle Card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Mode d'affichage :
            </span>
            <div className="flex bg-gray-100 dark:bg-gray-900 p-1 rounded-xl">
              <button
                onClick={() => setViewMode('local')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${viewMode === 'local'
                  ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/25'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
              >
                <FiHome className="w-4 h-4" />
                Mon Agence
              </button>
              {hasGlobalAccess ? (
                <button
                  onClick={() => setViewMode('global')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${viewMode === 'global'
                    ? 'bg-gradient-to-r from-brand-green to-emerald-600 text-white shadow-lg shadow-brand-green/25'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                >
                  <FiGlobe className="w-4 h-4" />
                  Toutes les Agences
                </button>
              ) : (
                <button
                  disabled
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-400 dark:text-gray-500 cursor-not-allowed"
                  title="Accès à la liste noire globale non accordé par le superadmin"
                >
                  <FiGlobe className="w-4 h-4" />
                  Toutes les Agences
                </button>
              )}
            </div>
          </div>

          {/* Agency Filter for Global View */}
          {viewMode === 'global' && (
            <div className="flex items-center gap-3">
              <FiFilter className="w-4 h-4 text-gray-400" />
              <select
                value={selectedAgency}
                onChange={(e) => setSelectedAgency(e.target.value)}
                className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-gray-900 dark:text-white cursor-pointer min-w-[200px]"
              >
                <option value="">Toutes les agences</option>
                {agencies.map((agency) => (
                  <option key={agency._id} value={agency._id}>
                    {agency.agencyName || agency.company} ({agency.blacklistCount} clients)
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Access Denied Message - Inside the card */}
        {!hasGlobalAccess && !checkingAccess && (
          <div className="mt-4 p-4 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/10 border border-amber-200 dark:border-amber-800 rounded-xl">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/50 rounded-lg">
                <FiAlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                  Accès à la liste noire globale non accordé
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                  {user?.role === 'superadmin'
                    ? "Votre agence n'a pas encore reçu l'autorisation d'accéder à la liste noire globale."
                    : "Contactez le superadmin pour demander l'accès."
                  }
                </p>
                {user?.role === 'superadmin' && (
                  <Link
                    to="/global-blacklist-access"
                    className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-100 rounded-lg hover:bg-primary-200 dark:bg-primary-900/40 dark:text-primary-300 dark:hover:bg-primary-900/60 transition-colors"
                  >
                    <FiShield className="w-3 h-3" />
                    Gérer les Accès
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Search and Add Button */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="relative flex-1 max-w-lg">
            <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={viewMode === 'local'
                ? "Rechercher un client blacklisté..."
                : "Rechercher par client ou agence..."
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all text-gray-900 dark:text-white placeholder-gray-400"
            />
          </div>

          {viewMode === 'local' && (
            <button
              onClick={() => setShowAddModal(true)}
              className="group flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white font-medium rounded-xl shadow-lg shadow-red-500/25 hover:shadow-xl hover:shadow-red-500/30 transition-all duration-300 hover:-translate-y-0.5"
            >
              <FiPlus className="w-5 h-5 transition-transform group-hover:rotate-90 duration-300" />
              Ajouter à la Liste Noire
            </button>
          )}
        </div>
      </div>

      {/* Blacklisted Clients Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        {/* Results count header */}
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/10 dark:to-rose-900/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/40 rounded-lg">
                <FiUserX className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  {viewMode === 'local' ? 'Clients Blacklistés de Mon Agence' : 'Clients Blacklistés - Toutes Agences'}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {blacklistedClients.length} client{blacklistedClients.length > 1 ? 's' : ''} trouvé{blacklistedClients.length > 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Client
                </th>
                {viewMode === 'global' && (
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Agence
                  </th>
                )}
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Raison
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Date d'Ajout
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Notes
                </th>
                {viewMode === 'local' && (
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {blacklistedClients.length === 0 ? (
                <tr>
                  <td colSpan={viewMode === 'global' ? 5 : 6} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center">
                        <FiUserCheck className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-gray-500 dark:text-gray-400 font-medium">Aucun client blacklisté trouvé</p>
                      <p className="text-sm text-gray-400 dark:text-gray-500">La liste est vide ou aucun résultat ne correspond à votre recherche</p>
                    </div>
                  </td>
                </tr>
              ) : (
                blacklistedClients.map((blacklist) => (
                  <tr key={blacklist._id} className="hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-red-100 to-rose-100 dark:from-red-900/40 dark:to-rose-900/40 rounded-xl flex items-center justify-center">
                          <FiUserX className="w-5 h-5 text-red-600 dark:text-red-400" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">
                            {blacklist.client?.name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {blacklist.client?.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    {viewMode === 'global' && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/40 rounded-lg flex items-center justify-center">
                            <FiHome className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {blacklist.agency?.name || blacklist.agency?.company}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {blacklist.agency?.email}
                            </div>
                          </div>
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-red-100 to-rose-100 text-red-700 dark:from-red-900/50 dark:to-rose-900/50 dark:text-red-300 border border-red-200 dark:border-red-800">
                        {blacklist.reason}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <FiCalendar className="w-4 h-4" />
                        {new Date(blacklist.blacklistedAt).toLocaleDateString('fr-FR')}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs">
                      <p className="truncate">{blacklist.notes || '-'}</p>
                    </td>
                    {viewMode === 'local' && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleRemoveFromBlacklist(blacklist._id)}
                          className="flex items-center gap-2 px-3 py-2 bg-brand-green/10 text-brand-green hover:bg-brand-green/20 rounded-lg transition-colors text-sm font-medium"
                          title="Retirer de la liste noire"
                        >
                          <FiUserCheck className="w-4 h-4" />
                          Retirer
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <nav className="inline-flex items-center gap-2 bg-white dark:bg-gray-800 rounded-2xl p-2 shadow-sm border border-gray-100 dark:border-gray-700">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-1 px-4 py-2 rounded-xl text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              <FiChevronLeft className="w-4 h-4" />
              Précédent
            </button>

            <div className="flex items-center gap-1 px-2">
              {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = idx + 1;
                } else if (currentPage <= 3) {
                  pageNum = idx + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + idx;
                } else {
                  pageNum = currentPage - 2 + idx;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-10 h-10 rounded-xl text-sm font-medium transition-all ${currentPage === pageNum
                      ? 'bg-gradient-to-r from-red-500 to-rose-600 text-white shadow-lg shadow-red-500/25'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="flex items-center gap-1 px-4 py-2 rounded-xl text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              Suivant
              <FiChevronRight className="w-4 h-4" />
            </button>
          </nav>
        </div>
      )}

      {/* Add to Blacklist Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-red-500 to-rose-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <FiUserX className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-white">
                    Ajouter à la Liste Noire
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setFormData({ clientId: '', reason: '', notes: '' });
                    setClientSearchTerm('');
                  }}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-5">
              {/* Client Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Client <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Rechercher un client..."
                    value={clientSearchTerm}
                    onChange={(e) => handleClientSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all text-gray-900 dark:text-white placeholder-gray-400"
                  />

                  {showClientDropdown && (
                    <div className="absolute z-10 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl max-h-60 overflow-auto">
                      {filteredClients.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                          Aucun client trouvé
                        </div>
                      ) : (
                        filteredClients.map((client) => (
                          <button
                            key={client._id}
                            onClick={() => handleClientSelect(client)}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-0"
                          >
                            <div className="font-medium text-gray-900 dark:text-white">
                              {client.name}
                            </div>
                            <div className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">
                              {client.email} • {client.phone}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Raison <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all text-gray-900 dark:text-white cursor-pointer"
                  required
                >
                  <option value="">Sélectionner une raison</option>
                  <option value="Non-paiement">Non-paiement</option>
                  <option value="Dégâts au véhicule">Dégâts au véhicule</option>
                  <option value="Conduite dangereuse">Conduite dangereuse</option>
                  <option value="Retard de retour">Retard de retour</option>
                  <option value="Fausses informations">Fausses informations</option>
                  <option value="Comportement inapproprié">Comportement inapproprié</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Notes (optionnel)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Détails supplémentaires..."
                  rows="3"
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all text-gray-900 dark:text-white placeholder-gray-400 resize-none"
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setFormData({ clientId: '', reason: '', notes: '' });
                  setClientSearchTerm('');
                }}
                className="px-5 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleAddToBlacklist}
                className="px-5 py-2.5 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white font-medium rounded-xl shadow-lg shadow-red-500/25 transition-all duration-300 flex items-center gap-2"
              >
                <FiUserX className="w-4 h-4" />
                Ajouter à la Liste Noire
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Blacklist;
